import type {
  AptitudeDomain,
  EventChoice,
  EventDefinition,
  EventEffects,
  PersonalStats,
} from "./types.ts";
import { semanticLifeTheme } from "./eventThemes.ts";

type DomainStat = keyof Pick<PersonalStats, "stamina" | "intellect" | "charm" | "empathy" | "luck">;

type LifeEventSeed = {
  id: string;
  topic: string;
  minAge: number;
  maxAge: number;
  title: string;
  text: string;
  focus: AptitudeDomain;
  secondary: AptitudeDomain;
  actions: [
    [label: string, result: string],
    [label: string, result: string],
    [label: string, result: string],
  ];
  cost?: number;
  exploreCost?: number;
  targetRelation?: "parent" | "partner" | "descendant";
  targetMinAge?: number;
  targetMaxAge?: number;
};

const DOMAIN_STAT: Record<AptitudeDomain, DomainStat> = {
  science: "intellect",
  language: "intellect",
  business: "charm",
  creative: "charm",
  sports: "stamina",
  care: "empathy",
  practical: "stamina",
  nature: "luck",
};

function shapedEffects(seed: LifeEventSeed, mode: "commit" | "explore" | "protect"): EventEffects {
  if (mode === "commit") {
    return {
      ...(seed.cost ? { assets: -seed.cost } : {}),
      stats: { [DOMAIN_STAT[seed.focus]]: 1, health: seed.focus === "sports" ? 1 : 0 },
      interests: [seed.focus],
      discoverTalents: [seed.focus],
      talentDelta: { [seed.focus]: 4 },
      traits: [`${seed.title}：投入`],
      longTermStates: { [`life_${seed.id}_path`]: "commit" },
    };
  }
  if (mode === "explore") {
    return {
      ...(seed.exploreCost !== undefined ? (seed.exploreCost ? { assets: -seed.exploreCost } : {}) : seed.cost ? { assets: -Math.max(1, Math.round(seed.cost * 0.4)) } : {}),
      stats: { [DOMAIN_STAT[seed.secondary]]: 1, health: 1 },
      interests: [seed.secondary],
      talentDelta: { [seed.secondary]: 3 },
      traits: [`${seed.title}：嘗試`],
      longTermStates: { [`life_${seed.id}_path`]: "explore" },
    };
  }
  return {
    stats: { health: 2, empathy: 1 },
    traits: [`${seed.title}：審慎`],
    longTermStates: { [`life_${seed.id}_path`]: "protect" },
  };
}

function lifeEvent(seed: LifeEventSeed): EventDefinition {
  const modes = ["commit", "explore", "protect"] as const;
  return {
    id: `life-choice-${seed.id}`,
    topic: seed.topic,
    ...semanticLifeTheme(seed.id, seed.topic),
    scope: "person",
    title: seed.title,
    text: seed.text,
    weight: 18,
    once: true,
    maxOccurrences: 1,
    topicCooldownYears: 1,
    targetRelation: seed.targetRelation,
    targetConditions: seed.targetRelation
      ? { minAge: seed.targetMinAge, maxAge: seed.targetMaxAge, alive: true }
      : undefined,
    conditions: { minAge: seed.minAge, maxAge: seed.maxAge },
    choices: seed.actions.map(([label, result], index) => ({
      id: modes[index],
      label,
      text: result,
      effects: shapedEffects(seed, modes[index]),
    })),
  };
}

const LIFE_EVENT_SEEDS: LifeEventSeed[] = [
  {
    id: "toddler-rain-window", topic: "curiosity", minAge: 0, maxAge: 2,
    title: "雨點在窗上留下路線", text: "你盯著玻璃上的水珠，手指一路追著它們滑下。家人發現，同樣的景象能讓你安靜很久。",
    focus: "nature", secondary: "creative",
    actions: [["抱到窗邊一起觀察", "家人陪你辨認雨聲與光影，讓好奇心有了安全的出口。"], ["拿紙筆留下顏色", "大人把水珠畫成線條，你也試著用手掌和顏料回應。"], ["關窗換到安靜房間", "家人先照顧你的作息，等精神穩定後再慢慢接觸新刺激。"]],
  },
  {
    id: "toddler-block-tower", topic: "making", minAge: 0, maxAge: 2,
    title: "積木塔倒下以後", text: "你把積木疊得歪歪斜斜，倒下時先愣住，接著又伸手想重來。",
    focus: "practical", secondary: "science",
    actions: [["陪著再蓋一次", "家人不替你完成，只在積木快滑落時提醒你看看底下。"], ["換不同形狀試試", "圓柱、方塊和長板混在一起，你開始注意它們站穩的方式。"], ["先收起來休息", "挫折正在累積，家人先讓你平靜，再把積木留到下次。"]],
  },
  {
    id: "toddler-song-response", topic: "expression", minAge: 0, maxAge: 2,
    title: "熟悉旋律響起時", text: "音樂一播放，你會跟著節奏搖晃，還試著模仿句尾的聲音。",
    focus: "language", secondary: "sports",
    actions: [["每天唱一小段", "家人把歌詞拆成短句，讓你在重複中找到聲音與意思。"], ["跟著節奏拍手走動", "旋律變成身體遊戲，你開始掌握快慢和停頓。"], ["維持自然接觸", "不特別安排課程，只在你有興致時一起聽。"]],
  },
  {
    id: "toddler-stranger-moment", topic: "relationship", minAge: 0, maxAge: 2,
    title: "陌生人伸來的手", text: "聚會上有人想抱你，你立刻縮回熟悉的大人懷裡，卻又忍不住偷看對方。",
    focus: "care", secondary: "language",
    actions: [["尊重你慢慢靠近", "家人先替你說明感受，讓你自己決定何時揮手或靠近。"], ["用玩具做中間橋梁", "對方把玩具放在地上，你從安全距離開始有了互動。"], ["先離開熱鬧場所", "家人帶你休息，不讓一次聚會變成勉強承受的壓力。"]],
  },
  {
    id: "toddler-puddle", topic: "adventure", minAge: 0, maxAge: 2,
    title: "門口那一小片水窪", text: "散步時你停在水窪前，鞋尖試探地碰了碰，眼神已經透露下一步。",
    focus: "sports", secondary: "nature",
    actions: [["換雨鞋讓你踩個夠", "水花濺起來，你在笑聲中練習平衡與控制力道。"], ["蹲下看看水裡有什麼", "你沒有跳進去，而是跟著大人找倒影、葉片和小石頭。"], ["繞過水窪繼續走", "今天的衣物和氣溫不合適，家人把冒險留到更安全的時候。"]],
  },
  {
    id: "toddler-sharing", topic: "family", minAge: 0, maxAge: 2,
    title: "點心盤只剩最後一塊", text: "你握住最後一塊餅乾，看看家人，又看看自己的手，第一次面對很小卻真實的取捨。",
    focus: "care", secondary: "business",
    actions: [["示範分成兩半", "家人不要求你全讓出去，而是讓你看見分享也能保留自己的份。"], ["讓你自己決定", "沒有人催促，你有時間觀察別人的表情並做出選擇。"], ["先滿足飢餓再談分享", "家人判斷你是真的餓了，先照顧需求，之後再練習輪流。"]],
  },
  {
    id: "preschool-cardboard-city", topic: "making", minAge: 3, maxAge: 5,
    title: "紙箱變成一座小城", text: "幾個紙箱堆在客廳，你說這裡有車站、醫院，還需要一條沒有人見過的路。",
    focus: "creative", secondary: "practical",
    actions: [["一起把城市做完整", "你負責想像，大人只協助剪裁，城裡逐漸有了自己的規則。"], ["先做一台能動的車", "你把注意力放在輪子和車身，反覆調整讓它真的滑起來。"], ["留下紙箱自由玩", "家人不設定成果，讓這座城市隨每天的故事改變。"]],
  },
  {
    id: "preschool-stage", topic: "expression", minAge: 3, maxAge: 5,
    title: "老師問誰願意站到前面", text: "班上要演一段短劇。你記得台詞，卻在大家看過來時突然不確定了。",
    focus: "language", secondary: "creative",
    actions: [["接下有台詞的角色", "你練習把話說清楚，也學會在忘詞時接住自己。"], ["改做道具和音效", "你留在舞台附近，用另一種方式讓故事成立。"], ["先當觀眾觀察一次", "你不被推上台，保留下一次主動舉手的空間。"]],
  },
  {
    id: "preschool-garden", topic: "nature", minAge: 3, maxAge: 5,
    title: "花盆裡冒出陌生的芽", text: "原本以為只是泥土，幾天後卻冒出一點綠色。你每天都想確認它有沒有長高。",
    focus: "nature", secondary: "science",
    actions: [["負責澆水並做記號", "你用歪歪的線記下高度，開始理解照顧和等待。"], ["用放大鏡觀察葉片", "葉脈、土壤和小蟲讓你提出一連串新的問題。"], ["交給大人照料", "你偶爾看看，不必把短暫好奇變成每天的責任。"]],
  },
  {
    id: "preschool-team-game", topic: "relationship", minAge: 3, maxAge: 5,
    title: "遊戲規則吵成一團", text: "大家都想當第一個出發的人，遊戲還沒開始，聲音已經越來越大。",
    focus: "business", secondary: "care",
    actions: [["提出輪流的辦法", "你試著讓每個人都有順序，第一次感受到規則能讓事情繼續。"], ["先問每個人想怎麼玩", "你沒有急著當裁判，而是把散亂的意見重新說一遍。"], ["離開爭吵換一個遊戲", "你保護自己的情緒，不必每次都負責讓所有人和好。"]],
  },
  {
    id: "preschool-lost-cat", topic: "adventure", minAge: 3, maxAge: 5,
    title: "巷口傳來細細的叫聲", text: "矮牆後像有小動物在叫。你想鑽過去看看，但那裡不在平常散步的路線上。",
    focus: "care", secondary: "nature",
    actions: [["請大人陪你一起找", "你學會先求助，再靠近可能需要幫忙的生命。"], ["站在外面觀察線索", "你從聲音、腳印和附近的食盆猜測牠去了哪裡。"], ["通知鄰居但不進去", "你沒有冒險跨過矮牆，仍把訊息交給能處理的大人。"]],
  },
  {
    id: "preschool-toy-budget", topic: "money", minAge: 3, maxAge: 5,
    title: "只能選一樣的玩具架", text: "架上有兩樣都很喜歡的東西，但家人說今天的預算只能帶走一樣。",
    focus: "business", secondary: "creative",
    actions: [["比較之後選最想要的", "你開始理解選擇一樣，也代表放下另一些可能。"], ["買材料回家自己做", "成品不會完全一樣，卻能依照你的想法慢慢改。"], ["今天先不買", "你把想要的東西記住，看看過幾天是否還是同樣在意。"]],
  },
  {
    id: "child-secret-path", topic: "adventure", minAge: 6, maxAge: 11,
    title: "放學路上的秘密小徑", text: "同學說穿過公園後方的小徑能更快回家，還保證只有少數人知道。",
    focus: "nature", secondary: "sports",
    actions: [["約大人先走一次", "你保留探索的興奮，也先確認出口、路況和安全時間。"], ["和同學結伴走主路旁段", "你只嘗試看得見人群的一段，練習判斷而不是逞強。"], ["照原路回家", "未知路線今天不值得冒險，你把好奇留到準備更完整時。"]],
  },
  {
    id: "child-science-fair", topic: "curiosity", minAge: 6, maxAge: 11,
    title: "科展題目卡在第一步", text: "你想測試不同紙張能承受多少重量，但第一次實驗就全部倒在桌上。",
    focus: "science", secondary: "practical",
    actions: [["重新設計測試方法", "你把變因一個個拆開，失敗開始變成可以閱讀的資料。"], ["找擅長動手的同學合作", "你負責提問，對方協助結構，作品因分工變得可靠。"], ["換成較簡單的題目", "你降低難度完成作品，也保留日後再挑戰的餘裕。"]],
  },
  {
    id: "child-club-choice", topic: "growth", minAge: 6, maxAge: 11,
    title: "社團體驗週的最後一天", text: "運動場、音樂教室和手作教室同時開放，你只能選一個地方留下來。",
    focus: "sports", secondary: "creative",
    actions: [["加入需要團隊合作的球隊", "你在固定練習裡學習位置、節奏與彼此補位。"], ["走進音樂與美術教室", "你開始用作品表達那些平常不容易說清楚的感受。"], ["先參加跨領域體驗社", "你保留探索期，不急著把一次選擇當成永久方向。"]],
  },
  {
    id: "child-bullying-witness", topic: "relationship", minAge: 6, maxAge: 11,
    title: "走廊角落的嘲笑聲", text: "幾個同學圍著一個人起鬨。你沒有被點名，卻很清楚自己看見了。",
    focus: "care", secondary: "language",
    actions: [["走過去把人帶開", "你承擔被注意的風險，讓對方先離開那個角落。"], ["記下情況告訴老師", "你用具體時間和行為求助，不讓事件只剩模糊的傳聞。"], ["先保護自己再匿名反映", "你不直接進入衝突，但仍找到不讓事情沉下去的方法。"]],
  },
  {
    id: "child-pocket-money", topic: "money", minAge: 6, maxAge: 11,
    title: "第一筆可以自己安排的零用錢", text: "錢放在小信封裡。想買的東西很多，家人卻只問你打算怎麼用。",
    focus: "business", secondary: "care",
    actions: [["分成儲蓄和使用兩份", "你第一次替未來的自己保留一部分選擇權。"], ["買材料完成一個計畫", "零用錢變成作品和經驗，而不是只停在抽屜裡。"], ["留一部分幫助別人", "你在自己的需要之外，也開始衡量什麼值得共同承擔。"]],
  },
  {
    id: "child-lost-wallet", topic: "ethics", minAge: 6, maxAge: 11,
    title: "長椅下的皮夾", text: "皮夾裡有現金和證件，附近卻沒有人回頭尋找。",
    focus: "care", secondary: "business",
    actions: [["交到派出所並留下線索", "你把發現的地點說清楚，理解誠實也需要完整的行動。"], ["和家人一起找失主", "你觀察證件與附近店家，在大人陪同下嘗試聯絡。"], ["原地等一會再處理", "你先確認是否有人返回，也避免獨自拿著皮夾走遠。"]],
  },
  {
    id: "teen-part-time", topic: "money", minAge: 12, maxAge: 17,
    title: "週末打工的邀請", text: "附近店家缺人，同學說工作不難，但排班會佔掉準備考試和休息的時間。",
    focus: "business", secondary: "practical",
    actions: [["固定每週排兩個班", "你開始接觸收入與責任，也必須重新安排讀書和睡眠。"], ["只接寒暑假的短期班", "你先體驗工作，不讓學期中的生活被完全打亂。"], ["暫時婉拒", "你保留時間處理眼前的學習和健康，不把忙碌當成成熟證明。"]],
  },
  {
    id: "teen-online-community", topic: "expression", minAge: 12, maxAge: 17,
    title: "作品在網路上收到回應", text: "你上傳的文字或作品突然被許多人看見，有人鼓勵，也有人用尖銳的話挑毛病。",
    focus: "creative", secondary: "language",
    actions: [["持續發表並學習回應", "你建立自己的創作節奏，也練習區分批評、建議與惡意。"], ["加入小型同好社群", "你把注意力放回可靠的交流，和少數人深入修改作品。"], ["先關閉通知休息", "你暫時離開評價聲浪，確認創作仍然是自己願意做的事。"]],
  },
  {
    id: "teen-night-exploration", topic: "adventure", minAge: 12, maxAge: 17,
    title: "朋友提議晚上去廢棄校舍", text: "大家說只是拍照，不會待太久。訊息群組很熱鬧，卻沒有人提到安全或許可。",
    focus: "sports", secondary: "nature",
    actions: [["改約白天合法開放的路線", "你沒有放棄探險，而是把地點、時間和返程方式談清楚。"], ["只參加附近的夜間散步", "你保留同伴活動，但不進入封閉或危險區域。"], ["拒絕這次邀請", "短暫被笑膽小，總比把自己交給沒人負責的計畫好。"]],
  },
  {
    id: "teen-contest", topic: "growth", minAge: 12, maxAge: 17,
    title: "老師遞來一張競賽報名表", text: "名額有限，準備期也不短。老師認為你有機會，但最後仍要由你決定。",
    focus: "science", secondary: "language",
    actions: [["組隊投入完整準備", "你接受高強度練習，學會把能力變成可交付的成果。"], ["先參加校內選拔", "你用較小的舞台確認興趣，再決定是否走得更遠。"], ["把時間留給原本計畫", "你不因別人的期待改道，繼續深化自己已選的方向。"]],
  },
  {
    id: "teen-volunteer", topic: "relationship", minAge: 12, maxAge: 17,
    title: "社區需要長期志工", text: "不是只去一天拍照，而是每個月都要到場。你能幫忙陪伴長輩、整理物資或教孩子功課。",
    focus: "care", secondary: "language",
    actions: [["固定陪伴同一群人", "關係在重複見面中累積，你也開始理解照顧不是一次性的善意。"], ["負責教學與活動設計", "你把知識整理成別人能理解的方式，練習表達和帶領。"], ["先參加單次支援", "你確認自己的時間和承受度，再決定能否做出長期承諾。"]],
  },
  {
    id: "teen-study-route", topic: "identity", minAge: 12, maxAge: 17,
    title: "選組方向和家人的期待不同", text: "你偏好的領域和家人認為穩定的方向不一樣。談話沒有吵起來，卻誰也沒有真正被說服。",
    focus: "creative", secondary: "business",
    actions: [["提出作品與具體計畫", "你不只說喜歡，也準備課程、時間與未來路線來承擔選擇。"], ["選擇能兼顧兩邊的組合", "你保留喜歡的領域，同時建立較務實的第二能力。"], ["先延後定案蒐集資訊", "你訪談學長姐並試上課程，讓決定不只由想像推動。"]],
  },
  {
    id: "young-mentor", topic: "career", minAge: 18, maxAge: 25,
    title: "前輩願意帶你做一個真正的案子", text: "這不是旁觀實習，你會負責一段能被檢驗的成果，也可能在公開場合犯錯。",
    focus: "practical", secondary: "business",
    actions: [["接下核心任務", "你承擔壓力和責任，開始累積能被說明的實戰經驗。"], ["先負責範圍較小的部分", "你跟著完整流程走一遍，再決定是否擴大責任。"], ["婉拒並補足基礎", "你承認準備不足，把時間用在真正缺少的能力上。"]],
  },
  {
    id: "young-lottery-window", topic: "money", minAge: 18, maxAge: 25,
    title: "便利商店彩券櫃前的停步", text: "頭獎新聞貼在玻璃上。朋友笑著說一起買一張，就當替平凡的一週加點想像。",
    focus: "business", secondary: "care",
    actions: [["只用小額買一張", "你把它當成娛樂支出，不借錢，也不追加預算。"], ["改把同樣金額存起來", "微小的選擇不會立刻致富，卻替長期習慣投下一票。"], ["請朋友喝飲料", "你把這筆錢換成當下的相處，不追逐幾乎碰不到的回報。"]],
    cost: 4,
    exploreCost: 0,
  },
  {
    id: "young-city-move", topic: "adventure", minAge: 18, maxAge: 25,
    title: "另一座城市寄來錄取通知", text: "機會比現在更接近理想，但房租、人際與生活都得重新開始。",
    focus: "language", secondary: "business",
    actions: [["搬去新城市試一年", "你承擔搬遷成本，在陌生環境重新建立工作與生活。"], ["先遠距合作三個月", "你降低一次性風險，用實際合作確認彼此是否適合。"], ["留在原地累積資源", "你放下這次機會，先把存款與能力準備得更完整。"]],
    cost: 55,
  },
  {
    id: "young-side-project", topic: "making", minAge: 18, maxAge: 25,
    title: "下班後的小計畫開始有人使用", text: "原本只是興趣做的工具或作品，現在有人詢問能否付費取得更完整版本。",
    focus: "science", secondary: "creative",
    actions: [["投入固定時間正式製作", "你建立版本與時程，興趣開始接受真實使用者的檢驗。"], ["和擅長營運的人合作", "你保留創作角色，也學會讓產品被看見和被使用。"], ["維持非營利興趣", "你不把每份熱愛都變成工作，保留它原本帶來的自由。"]],
  },
  {
    id: "young-relationship-distance", topic: "relationship", minAge: 18, maxAge: 25,
    title: "重要的人即將去遠方", text: "彼此都沒有做錯事，只是新的學業或工作讓距離突然成為現實。",
    focus: "care", secondary: "language",
    actions: [["約定具體的遠距方式", "你們談清楚見面、聯絡和各自生活，不只靠一句等我。"], ["先維持開放的相處", "你們降低承諾強度，讓未來由真實相處而非恐懼決定。"], ["好好道別", "你承認方向已經不同，不用用拖延維持關係的外形。"]],
  },
  {
    id: "young-study-abroad", topic: "education", minAge: 18, maxAge: 25,
    title: "短期海外課程的名額", text: "課程能拓展視野，但費用不低，也會打亂原本的工作或學習安排。",
    focus: "language", secondary: "science",
    actions: [["使用存款參加完整課程", "你把資源換成密集的語言與跨文化經驗。"], ["申請補助後再決定", "你多花時間準備資料，降低家庭需要承擔的成本。"], ["選擇本地替代課程", "你保留原來的生活節奏，用較低成本深化同一能力。"]],
    cost: 90,
  },
  {
    id: "adult-business-proposal", topic: "money", minAge: 26, maxAge: 44,
    title: "熟人帶來一份共同創業提案", text: "計畫有市場資料，也有尚未回答的風險。對方希望你投入的不只是錢，還包括時間與信用。",
    focus: "business", secondary: "science",
    actions: [["小額入股並參與營運", "你設定投入上限和權責，讓合作接受帳目與里程碑檢驗。"], ["先做三個月市場測試", "你們縮小規模，用真實訂單確認需求而不是相信簡報。"], ["拒絕資金往來", "你保留關係，但不讓人情代替風險判斷。"]],
    cost: 120,
  },
  {
    id: "adult-health-routine", topic: "health", minAge: 26, maxAge: 44,
    title: "檢查報告出現幾個警示數字", text: "還不到疾病，但醫師說現在的睡眠、飲食和壓力若不改，幾年後可能不同。",
    focus: "sports", secondary: "care",
    actions: [["加入有教練的規律訓練", "你支付費用換取可持續的計畫，也重新安排工作邊界。"], ["和家人一起調整生活", "改變不只落在一個人身上，餐桌與休息時間一起被重整。"], ["先從最小習慣開始", "你不做激烈承諾，只固定散步、睡眠和下次檢查。"]],
    cost: 35,
  },
  {
    id: "adult-home-choice", topic: "family", minAge: 26, maxAge: 44,
    title: "現有住處開始不夠用", text: "空間、通勤和財務無法同時完美，家庭需要決定接下來幾年的生活重心。",
    focus: "business", secondary: "care",
    actions: [["搬到空間更大的區域", "家庭承擔搬遷與更高支出，換取能長期使用的生活空間。"], ["整理並改造現有住處", "你們用較小成本重新分配空間，也得接受部分限制。"], ["暫時維持原狀", "資金保留下來，家庭先用更清楚的規則共享有限空間。"]],
    cost: 140,
  },
  {
    id: "adult-community-role", topic: "relationship", minAge: 26, maxAge: 44,
    title: "社區推舉你負責共同事務", text: "這份工作沒有高報酬，卻會影響許多人的日常，也可能讓你捲入意見衝突。",
    focus: "care", secondary: "business",
    actions: [["接下任期並公開流程", "你建立會議與紀錄，嘗試讓不同立場仍能一起做事。"], ["只負責一項專案", "你選擇能力所及的範圍，不把整個社區扛在自己身上。"], ["婉拒正式職務", "你保留私人時間，改以偶爾協助的方式參與。"]],
  },
  {
    id: "adult-retraining", topic: "career", minAge: 26, maxAge: 44,
    title: "原本的技能正在被新工具改變", text: "工作還在，但熟悉的做法逐漸失去優勢。公司提供一個需要夜間上課的轉型名額。",
    focus: "science", secondary: "language",
    actions: [["完成完整轉型課程", "你犧牲一段休息時間，換取能跨到新職務的能力。"], ["先學最直接相關的模組", "你把學習拆小，先改善目前工作再評估下一步。"], ["深化不容易被取代的專長", "你不追逐每個新工具，轉而強化判斷、溝通與經驗。"]],
    cost: 45,
  },
  {
    id: "adult-family-debt", topic: "ethics", minAge: 26, maxAge: 44,
    title: "朋友陷入債務週轉困境", text: "一位相識多年的朋友拿出帳單和催繳通知，希望你幫忙，但任何承諾都可能改變彼此關係。",
    focus: "business", secondary: "care",
    actions: [["提供一次性的有限援助", "你先說清楚金額不會追加，也不替任何新債務簽名。"], ["陪同重整債務方案", "你投入時間整理帳目，尋找不需要私人作保的路。"], ["拒絕金錢往來", "關係可能一時緊張，但你不讓整個家庭暴露在未知負債下。"]],
    cost: 36,
  },
  {
    id: "midlife-second-career", topic: "career", minAge: 45, maxAge: 64,
    title: "熟悉的職涯已經走到分岔口", text: "你仍能繼續原工作，也有人邀請你把多年經驗帶到完全不同的領域。",
    focus: "language", secondary: "business",
    actions: [["接受轉換並重新學習", "你放下部分資歷，換取新的工作內容與成長空間。"], ["先以顧問方式合作", "你保留本業收入，用有限案量測試新方向。"], ["留在原位做到更穩", "你不因新鮮感轉彎，改把經驗留給團隊與後進。"]],
  },
  {
    id: "midlife-parent-care", topic: "family", minAge: 45, maxAge: 64,
    title: "長輩開始需要更固定的照顧", text: "偶爾陪診已經不夠。時間、費用與家庭分工必須被攤開來談。",
    focus: "care", secondary: "business",
    actions: [["調整工作親自分擔", "你換取更多陪伴時間，也承受收入和體力上的壓力。"], ["安排專業照護並共同出資", "家庭用資金換取穩定照顧，也保留彼此原有生活。"], ["召集家人重新分工", "你不默默接下全部責任，讓每個人的能力與限制被看見。"]],
    cost: 75,
  },
  {
    id: "midlife-screening", topic: "health", minAge: 45, maxAge: 64,
    title: "完整健康檢查的建議", text: "目前沒有明顯不適，但家族病史讓醫師建議做更深入的檢查。",
    focus: "science", secondary: "sports",
    actions: [["安排完整檢查", "你支付較高費用，換取提早發現風險與具體追蹤計畫。"], ["先做關鍵項目", "你依家族病史選擇必要檢查，把費用控制在可承擔範圍。"], ["維持日常追蹤", "你暫不做進階檢查，承諾規律運動並留意任何變化。"]],
    cost: 60,
  },
  {
    id: "midlife-late-business", topic: "money", minAge: 45, maxAge: 64,
    title: "多年專長能否變成自己的事業", text: "客戶與人脈已經存在，真正的不確定是離開固定收入後，訂單能否長期維持。",
    focus: "business", secondary: "practical",
    actions: [["成立小型工作室", "你準備半年資金並控制規模，正式承擔收入波動。"], ["先接固定數量的外部案件", "你測試市場與生活負荷，不急著一次離開原職。"], ["維持受雇並整理方法", "你把經驗變成教材或流程，保留日後轉型的可能。"]],
    cost: 130,
  },
  {
    id: "midlife-adult-child", topic: "relationship", minAge: 45, maxAge: 64,
    title: "成年子女做了你不認同的決定", text: "{relative} 的選擇會影響自己的人生，卻已不再由你替他決定。你們仍需要找到能說話的位置。",
    focus: "care", secondary: "language",
    targetRelation: "descendant", targetMinAge: 18,
    actions: [["先聽完計畫再說擔心", "你把控制改成提問，讓對方也必須說明風險與責任。"], ["提供有限且清楚的支持", "你說明能幫到哪裡，不用金錢或情緒換取服從。"], ["保留距離讓他承擔", "你不贊同也不破壞關係，讓結果成為他自己的經驗。"]],
  },
  {
    id: "midlife-legacy-project", topic: "identity", minAge: 45, maxAge: 64,
    title: "想做很久的計畫仍放在抽屜裡", text: "它可能是一本書、一門技藝或一趟研究。時間沒有消失，只是每年都被更急的事拿走。",
    focus: "creative", secondary: "nature",
    actions: [["排出一年的完成計畫", "你把願望拆成每月能交付的成果，正式為它留下時間。"], ["找同伴一起進行", "共同約定讓計畫不再只靠一時熱情支撐。"], ["承認現在不是時機", "你整理資料並設定重新評估日期，不再用模糊的改天欺騙自己。"]],
  },
  {
    id: "later-mentor", topic: "relationship", minAge: 65, maxAge: 100,
    title: "年輕人帶著問題來找你", text: "對方真正需要的可能不是答案，而是有人願意把走過的彎路說清楚。",
    focus: "language", secondary: "care",
    actions: [["固定每月陪他討論", "你分享經驗也聽見新世代的做法，關係逐漸成為雙向學習。"], ["整理一份實用指南", "你把零散經驗寫成別人能重複使用的方法。"], ["只回答眼前這一題", "你提供有限幫助，不讓新的責任佔滿退休生活。"]],
  },
  {
    id: "later-community-class", topic: "growth", minAge: 65, maxAge: 100,
    title: "社區教室開了一門陌生課", text: "同學年齡各不相同，內容也和過去職業毫無關係。報名表就在手上。",
    focus: "creative", secondary: "science",
    actions: [["報名完整一期", "你允許自己重新當初學者，在作品和錯誤裡建立新節奏。"], ["先參加單堂體驗", "你確認身體與興趣是否適合，再決定是否長期投入。"], ["選擇熟悉的活動", "你維持原本社交和生活方式，不為了證明活躍而勉強改變。"]],
  },
  {
    id: "later-travel", topic: "adventure", minAge: 65, maxAge: 100,
    title: "一直想去的地方終於有空檔", text: "體力、費用與安全都需要考慮，但等待更完美的時機也可能永遠不會出發。",
    focus: "nature", secondary: "language",
    actions: [["安排節奏寬鬆的長途旅行", "你購買合適保障並減少行程，把未知變成可承擔的冒險。"], ["先完成附近的小旅行", "你用短程測試體力和裝備，再決定是否走得更遠。"], ["把旅程改成在地探索", "你不遠行，仍以新的眼光重新認識生活多年的地方。"]],
    cost: 80,
  },
  {
    id: "later-memoir", topic: "expression", minAge: 65, maxAge: 100,
    title: "舊照片喚回一段沒人知道的故事", text: "照片上的人有些已不在，晚輩只知道名字，不知道當時為何做出那些選擇。",
    focus: "language", secondary: "creative",
    actions: [["寫成完整家族回憶", "你查證年份、訪問親友，讓記憶成為後代能閱讀的故事。"], ["錄下幾段口述", "你不追求完整，先保留聲音、語氣和最重要的片段。"], ["只和家人聊這一晚", "故事留在親密談話裡，不一定每段人生都要被整理成作品。"]],
  },
  {
    id: "later-health-procedure", topic: "health", minAge: 65, maxAge: 100,
    title: "治療能改善生活，也有恢復風險", text: "醫師提出積極治療、保守控制和持續觀察三條路，每條都需要承擔不同代價。",
    focus: "science", secondary: "care",
    actions: [["接受治療並安排復健", "你在家人陪同下準備恢復期，用短期辛苦換取改善機會。"], ["採取保守治療", "你控制症狀並定期追蹤，盡量維持熟悉的生活節奏。"], ["先尋求第二意見", "你延後決定，帶著完整資料確認風險和替代方案。"]],
    cost: 70,
  },
  {
    id: "later-family-gift", topic: "money", minAge: 65, maxAge: 100,
    title: "是否提前把資源交給晚輩", text: "{relative} 正準備學業或事業，你的支持能帶來幫助，也可能削弱自己的晚年保障。",
    focus: "business", secondary: "care",
    targetRelation: "descendant", targetMinAge: 16,
    actions: [["設定上限後提供支持", "你保留醫療與生活預備金，只把可承擔的部分提早交付。"], ["以經驗和人脈協助", "你不直接給錢，而是陪對方檢查計畫並連結可靠資源。"], ["暫不分配資產", "你把晚年安全放在前面，讓未來繼承依既定安排進行。"]],
    cost: 100,
  },
];

export const LIFE_DIVERSITY_EVENTS = LIFE_EVENT_SEEDS.map(lifeEvent);

const qualified = (
  description: string,
  anyOf: NonNullable<EventChoice["requires"]>["anyOf"],
): EventChoice["requires"] => ({ description, anyOf });

export const EXPANDED_CAREER_ENTRY_CHOICES: EventChoice[] = [
  {
    id: "technology", label: "科技業・軟體與資料專員", text: "年收入 132，職級 1；智力 +4、健康 -1。需要科學興趣、突出科學天賦或智力 68。",
    requires: qualified("需要科學興趣、科學天賦 68，或智力 68", [{ kind: "interest", domain: "science" }, { kind: "talent", domain: "science", min: 68 }, { kind: "stat", stat: "intellect", min: 68 }]),
    effects: { stats: { intellect: 4, health: -1 }, traits: ["科技專業"], flags: ["career-started"], career: { industry: "科技業", occupation: "軟體與資料專員", status: "employed", level: 1, experience: 0, annualIncome: 132 } },
  },
  {
    id: "finance", label: "金融業・風險與理財專員", text: "年收入 128，職級 1；智力 +3、魅力 +2。需要商業興趣、突出商業天賦或智力 66。",
    requires: qualified("需要商業興趣、商業天賦 68，或智力 66", [{ kind: "interest", domain: "business" }, { kind: "talent", domain: "business", min: 68 }, { kind: "stat", stat: "intellect", min: 66 }]),
    effects: { stats: { intellect: 3, charm: 2 }, traits: ["金融專業"], flags: ["career-started"], career: { industry: "金融業", occupation: "風險與理財專員", status: "employed", level: 1, experience: 0, annualIncome: 128 } },
  },
  {
    id: "healthcare", label: "醫療照護・健康服務人員", text: "年收入 120，職級 1；共感 +4、體力 -1。需要照護興趣、照護天賦或共感 66。",
    requires: qualified("需要照護興趣、照護天賦 68，或共感 66", [{ kind: "interest", domain: "care" }, { kind: "talent", domain: "care", min: 68 }, { kind: "stat", stat: "empathy", min: 66 }]),
    effects: { stats: { empathy: 4, stamina: -1 }, traits: ["照護專業"], flags: ["career-started"], career: { industry: "醫療照護", occupation: "健康服務人員", status: "employed", level: 1, experience: 0, annualIncome: 120 } },
  },
  {
    id: "education", label: "教育業・教學與培訓人員", text: "年收入 112，職級 1；智力 +2、共感 +3。需要語言興趣、語言天賦或共感 64。",
    requires: qualified("需要語言興趣、語言天賦 68，或共感 64", [{ kind: "interest", domain: "language" }, { kind: "talent", domain: "language", min: 68 }, { kind: "stat", stat: "empathy", min: 64 }]),
    effects: { stats: { intellect: 2, empathy: 3 }, traits: ["教育專業"], flags: ["career-started"], career: { industry: "教育業", occupation: "教學與培訓人員", status: "employed", level: 1, experience: 0, annualIncome: 112 } },
  },
  {
    id: "creative", label: "文化創意・內容企劃", text: "年收入 104，職級 1；魅力 +4、運氣 +2。需要創意興趣、創意天賦或魅力 66。",
    requires: qualified("需要創意興趣、創意天賦 68，或魅力 66", [{ kind: "interest", domain: "creative" }, { kind: "talent", domain: "creative", min: 68 }, { kind: "stat", stat: "charm", min: 66 }]),
    effects: { stats: { charm: 4, luck: 2 }, traits: ["創意專業"], flags: ["career-started"], career: { industry: "文化創意", occupation: "內容企劃", status: "employed", level: 1, experience: 0, annualIncome: 104 } },
  },
  {
    id: "skilled-trade", label: "技術工藝・設備技術人員", text: "年收入 118，職級 1；體力 +3、智力 +2。需要實作興趣、實作天賦或體力 64。",
    requires: qualified("需要實作興趣、實作天賦 68，或體力 64", [{ kind: "interest", domain: "practical" }, { kind: "talent", domain: "practical", min: 68 }, { kind: "stat", stat: "stamina", min: 64 }]),
    effects: { stats: { stamina: 3, intellect: 2 }, traits: ["技術專業"], flags: ["career-started"], career: { industry: "技術工藝", occupation: "設備技術人員", status: "employed", level: 1, experience: 0, annualIncome: 118 } },
  },
  {
    id: "environment", label: "環境產業・生態調查人員", text: "年收入 108，職級 1；智力 +2、運氣 +3。需要自然興趣、自然天賦或智力 64。",
    requires: qualified("需要自然興趣、自然天賦 68，或智力 64", [{ kind: "interest", domain: "nature" }, { kind: "talent", domain: "nature", min: 68 }, { kind: "stat", stat: "intellect", min: 64 }]),
    effects: { stats: { intellect: 2, luck: 3 }, traits: ["環境專業"], flags: ["career-started"], career: { industry: "環境產業", occupation: "生態調查人員", status: "employed", level: 1, experience: 0, annualIncome: 108 } },
  },
  {
    id: "sports", label: "運動產業・體能與活動教練", text: "年收入 106，職級 1；體力 +4、魅力 +2。需要運動興趣、運動天賦或體力 72。",
    requires: qualified("需要運動興趣、運動天賦 68，或體力 72", [{ kind: "interest", domain: "sports" }, { kind: "talent", domain: "sports", min: 68 }, { kind: "stat", stat: "stamina", min: 72 }]),
    effects: { stats: { stamina: 4, charm: 2 }, traits: ["運動專業"], flags: ["career-started"], career: { industry: "運動產業", occupation: "體能與活動教練", status: "employed", level: 1, experience: 0, annualIncome: 106 } },
  },
  {
    id: "public-service", label: "公共服務・行政企劃人員", text: "年收入 110，職級 1；共感 +2、智力 +2。需要共感 62、語言興趣或商業興趣。",
    requires: qualified("需要共感 62、語言興趣或商業興趣", [{ kind: "stat", stat: "empathy", min: 62 }, { kind: "interest", domain: "language" }, { kind: "interest", domain: "business" }]),
    effects: { stats: { empathy: 2, intellect: 2 }, traits: ["公共服務"], flags: ["career-started"], career: { industry: "公共服務", occupation: "行政企劃人員", status: "employed", level: 1, experience: 0, annualIncome: 110 } },
  },
  {
    id: "hospitality", label: "旅宿餐飲・服務營運人員", text: "年收入 102，職級 1；魅力 +3、體力 +1。需要魅力 60、照護興趣或商業興趣。",
    requires: qualified("需要魅力 60、照護興趣或商業興趣", [{ kind: "stat", stat: "charm", min: 60 }, { kind: "interest", domain: "care" }, { kind: "interest", domain: "business" }]),
    effects: { stats: { charm: 3, stamina: 1 }, traits: ["服務專業"], flags: ["career-started"], career: { industry: "旅宿餐飲", occupation: "服務營運人員", status: "employed", level: 1, experience: 0, annualIncome: 102 } },
  },
  {
    id: "general-office", label: "一般企業・行政助理", text: "年收入 96，職級 1；智力 +2。資格門檻較低，可先累積通用工作經驗。",
    effects: { stats: { intellect: 2 }, traits: ["行政實務"], flags: ["career-started"], career: { industry: "一般企業", occupation: "行政助理", status: "employed", level: 1, experience: 0, annualIncome: 96 } },
  },
  {
    id: "general-service", label: "生活服務・門市人員", text: "年收入 90，職級 1；魅力 +2、體力 +1。資格門檻較低，可從現場經驗起步。",
    effects: { stats: { charm: 2, stamina: 1 }, traits: ["現場服務"], flags: ["career-started"], career: { industry: "生活服務", occupation: "門市人員", status: "employed", level: 1, experience: 0, annualIncome: 90 } },
  },
  {
    id: "legal-policy", label: "法律政策・法務與政策助理", text: "年收入 124，職級 1；智力 +4、共感 +1。需要語言興趣、商業興趣或智力 68。",
    requires: qualified("需要語言興趣、商業興趣或智力 68", [{ kind: "interest", domain: "language" }, { kind: "interest", domain: "business" }, { kind: "stat", stat: "intellect", min: 68 }]),
    effects: { stats: { intellect: 4, empathy: 1 }, traits: ["法務政策"], flags: ["career-started"], career: { industry: "法律政策", occupation: "法務與政策助理", status: "employed", level: 1, experience: 0, annualIncome: 124 } },
  },
  {
    id: "architecture", label: "建築營造・設計與工務人員", text: "年收入 122，職級 1；體力 +2、智力 +3。需要實作興趣、創意興趣或實作天賦 68。",
    requires: qualified("需要實作興趣、創意興趣或實作天賦 68", [{ kind: "interest", domain: "practical" }, { kind: "interest", domain: "creative" }, { kind: "talent", domain: "practical", min: 68 }]),
    effects: { stats: { stamina: 2, intellect: 3 }, traits: ["建築實務"], flags: ["career-started"], career: { industry: "建築營造", occupation: "設計與工務人員", status: "employed", level: 1, experience: 0, annualIncome: 122 } },
  },
  {
    id: "agriculture-food", label: "農業食品・生產與品管人員", text: "年收入 108，職級 1；體力 +3、智力 +2。需要自然興趣、實作興趣或自然天賦 68。",
    requires: qualified("需要自然興趣、實作興趣或自然天賦 68", [{ kind: "interest", domain: "nature" }, { kind: "interest", domain: "practical" }, { kind: "talent", domain: "nature", min: 68 }]),
    effects: { stats: { stamina: 3, intellect: 2 }, traits: ["食品生產"], flags: ["career-started"], career: { industry: "農業食品", occupation: "生產與品管人員", status: "employed", level: 1, experience: 0, annualIncome: 108 } },
  },
  {
    id: "logistics", label: "物流運輸・供應鏈調度人員", text: "年收入 116，職級 1；智力 +2、體力 +2。需要商業興趣、實作興趣或智力 62。",
    requires: qualified("需要商業興趣、實作興趣或智力 62", [{ kind: "interest", domain: "business" }, { kind: "interest", domain: "practical" }, { kind: "stat", stat: "intellect", min: 62 }]),
    effects: { stats: { intellect: 2, stamina: 2 }, traits: ["供應鏈實務"], flags: ["career-started"], career: { industry: "物流運輸", occupation: "供應鏈調度人員", status: "employed", level: 1, experience: 0, annualIncome: 116 } },
  },
  {
    id: "media", label: "媒體傳播・採訪與編輯人員", text: "年收入 105，職級 1；智力 +3、魅力 +3。需要語言興趣、創意興趣或魅力 65。",
    requires: qualified("需要語言興趣、創意興趣或魅力 65", [{ kind: "interest", domain: "language" }, { kind: "interest", domain: "creative" }, { kind: "stat", stat: "charm", min: 65 }]),
    effects: { stats: { intellect: 3, charm: 3 }, traits: ["媒體採編"], flags: ["career-started"], career: { industry: "媒體傳播", occupation: "採訪與編輯人員", status: "employed", level: 1, experience: 0, annualIncome: 105 } },
  },
  {
    id: "social-work", label: "社會服務・個案與社區工作者", text: "年收入 101，職級 1；共感 +5、健康 -1。需要照護興趣、語言興趣或共感 66。",
    requires: qualified("需要照護興趣、語言興趣或共感 66", [{ kind: "interest", domain: "care" }, { kind: "interest", domain: "language" }, { kind: "stat", stat: "empathy", min: 66 }]),
    effects: { stats: { empathy: 5, health: -1 }, traits: ["社會服務"], flags: ["career-started"], career: { industry: "社會服務", occupation: "個案與社區工作者", status: "employed", level: 1, experience: 0, annualIncome: 101 } },
  },
  {
    id: "tourism", label: "觀光旅遊・行程與接待企劃", text: "年收入 104，職級 1；魅力 +4、智力 +1。需要語言興趣、自然興趣或魅力 62。",
    requires: qualified("需要語言興趣、自然興趣或魅力 62", [{ kind: "interest", domain: "language" }, { kind: "interest", domain: "nature" }, { kind: "stat", stat: "charm", min: 62 }]),
    effects: { stats: { charm: 4, intellect: 1 }, traits: ["旅遊企劃"], flags: ["career-started"], career: { industry: "觀光旅遊", occupation: "行程與接待企劃", status: "employed", level: 1, experience: 0, annualIncome: 104 } },
  },
  {
    id: "research", label: "研究機構・研究助理", text: "年收入 114，職級 1；智力 +5、健康 -2。需要科學興趣、語言興趣或智力 70。",
    requires: qualified("需要科學興趣、語言興趣或智力 70", [{ kind: "interest", domain: "science" }, { kind: "interest", domain: "language" }, { kind: "stat", stat: "intellect", min: 70 }]),
    effects: { stats: { intellect: 5, health: -2 }, traits: ["研究訓練"], flags: ["career-started"], career: { industry: "研究機構", occupation: "研究助理", status: "employed", level: 1, experience: 0, annualIncome: 114 } },
  },
  {
    id: "real-estate-sales", label: "不動產服務・業務顧問", text: "年收入 98，職級 1；魅力 +4、運氣 +2，收入成長較依賴業績。需要商業興趣或魅力 64。",
    requires: qualified("需要商業興趣或魅力 64", [{ kind: "interest", domain: "business" }, { kind: "stat", stat: "charm", min: 64 }]),
    effects: { stats: { charm: 4, luck: 2 }, traits: ["業績導向"], flags: ["career-started"], career: { industry: "不動產服務", occupation: "業務顧問", status: "employed", level: 1, experience: 0, annualIncome: 98 } },
  },
  {
    id: "manufacturing", label: "製造產業・製程與品質人員", text: "年收入 120，職級 1；體力 +2、智力 +3。需要實作興趣、科學興趣或體力 62。",
    requires: qualified("需要實作興趣、科學興趣或體力 62", [{ kind: "interest", domain: "practical" }, { kind: "interest", domain: "science" }, { kind: "stat", stat: "stamina", min: 62 }]),
    effects: { stats: { stamina: 2, intellect: 3 }, traits: ["製程品質"], flags: ["career-started"], career: { industry: "製造產業", occupation: "製程與品質人員", status: "employed", level: 1, experience: 0, annualIncome: 120 } },
  },
  {
    id: "beauty-wellness", label: "美容健康・造型與顧客服務", text: "年收入 99，職級 1；魅力 +4、體力 +1。需要創意興趣、照護興趣或魅力 60。",
    requires: qualified("需要創意興趣、照護興趣或魅力 60", [{ kind: "interest", domain: "creative" }, { kind: "interest", domain: "care" }, { kind: "stat", stat: "charm", min: 60 }]),
    effects: { stats: { charm: 4, stamina: 1 }, traits: ["造型服務"], flags: ["career-started"], career: { industry: "美容健康", occupation: "造型與顧客服務", status: "employed", level: 1, experience: 0, annualIncome: 99 } },
  },
  {
    id: "freelance", label: "自由接案・綜合專案工作者", text: "起始年收入 88，職級 1；運氣 +3、智力 +2，收入較不穩定。需要任一已發現天賦或兩項興趣。",
    requires: qualified("需要任一突出天賦或相關興趣", [{ kind: "talent", domain: "creative", min: 68 }, { kind: "talent", domain: "practical", min: 68 }, { kind: "interest", domain: "creative" }, { kind: "interest", domain: "practical" }]),
    effects: { stats: { luck: 3, intellect: 2 }, traits: ["自由工作"], flags: ["career-started"], career: { industry: "自由接案", occupation: "綜合專案工作者", status: "self-employed", level: 1, experience: 0, annualIncome: 88 } },
  },
];

export const CAREER_PATH_EVENTS: EventDefinition[] = [
  {
    id: "career-path-certification", topic: "career", scope: "person", title: "專業證照能打開下一道門",
    text: "公司願意認列證照，但準備期會占用晚上與週末。這不是免費升職，而是一段可衡量的投資。",
    weight: 13, repeatable: true, cooldownYears: 6, maxOccurrences: 2,
    conditions: { minAge: 23, maxAge: 58, careerStatuses: ["employed", "self-employed"], minCareerExperience: 1 },
    choices: [
      { id: "complete", label: "報名完整課程與考試", text: "家庭資金 -55；智力 +5，年收入 +14，並取得專業認證。", effects: { assets: -55, stats: { intellect: 5, health: -2 }, careerIncomeDelta: 14, traits: ["專業認證"] }, requires: { householdAssetsMin: 55 } },
      { id: "self-study", label: "利用空檔自行準備", text: "不支付課程費；智力 +3、健康 -1，年收入 +6。", effects: { stats: { intellect: 3, health: -1 }, careerIncomeDelta: 6, traits: ["自學進修"] } },
      { id: "skip", label: "把時間留給現有工作", text: "收入與職級不變；健康 +2，暫不追求證照路線。", effects: { stats: { health: 2 }, traits: ["維持專注"] } },
    ],
  },
  {
    id: "career-path-promotion", topic: "career", scope: "person", title: "升職機會附帶新的責任",
    text: "職稱和收入都會提高，但你需要處理人員、預算與失敗時的責任。",
    weight: 13, repeatable: true, cooldownYears: 5, maxOccurrences: 4,
    conditions: { minAge: 24, maxAge: 60, careerStatus: "employed", minCareerExperience: 2, maxCareerLevel: 5 },
    choices: [
      { id: "lead", label: "接下管理責任", text: "職級 +1、年收入 +22；魅力 +3、健康 -3。", effects: { careerLevelDelta: 1, careerIncomeDelta: 22, stats: { charm: 3, health: -3 }, traits: ["帶領團隊"] } },
      { id: "expert", label: "改走資深專業路線", text: "職級 +1、年收入 +16；智力 +4、健康 -1。", effects: { careerLevelDelta: 1, careerIncomeDelta: 16, stats: { intellect: 4, health: -1 }, traits: ["資深專家"] } },
      { id: "decline", label: "維持職務與生活邊界", text: "職級與收入不變；健康 +3，不承擔額外管理工作。", effects: { stats: { health: 3 }, traits: ["工作邊界"] } },
    ],
  },
  {
    id: "career-path-industry-switch", topic: "career", scope: "person", title: "另一個業界願意接住你的經驗",
    text: "新職位不會完全從零開始，但你需要放下一部分熟悉的人脈與流程。",
    weight: 10, repeatable: true, cooldownYears: 8, maxOccurrences: 2,
    conditions: { minAge: 25, maxAge: 52, careerStatus: "employed", minCareerExperience: 3 },
    choices: [
      { id: "switch", label: "轉往新業界", text: "年收入先 -8、職級維持；智力 +4、運氣 +3，兩年內重新累積適應力。", effects: { career: { industry: "跨域專業", occupation: "轉型專員" }, careerIncomeDelta: -8, stats: { intellect: 4, luck: 3 }, traits: ["跨域轉型"] } },
      { id: "project", label: "先以跨部門專案合作", text: "年收入 +5；魅力 +2、智力 +2，保留原職觀察新領域。", effects: { careerIncomeDelta: 5, stats: { charm: 2, intellect: 2 }, traits: ["跨域合作"] } },
      { id: "stay", label: "留在原業界深化", text: "年收入 +4；智力 +2，放棄本次轉換風險。", effects: { careerIncomeDelta: 4, stats: { intellect: 2 }, traits: ["深耕原業"] } },
    ],
  },
  {
    id: "career-path-workplace-conflict", topic: "career", scope: "person", title: "重要專案的方向出現衝突",
    text: "主管要求快點交付，同事擔心品質，而最後簽名的人會是你。",
    weight: 11, repeatable: true, cooldownYears: 5, maxOccurrences: 4,
    conditions: { minAge: 22, maxAge: 62, careerStatuses: ["employed", "self-employed"], minCareerExperience: 1 },
    choices: [
      { id: "negotiate", label: "提出分階段交付方案", text: "智力 +3、魅力 +2；年收入 +5，建立能兼顧時程與品質的紀錄。", effects: { stats: { intellect: 3, charm: 2 }, careerIncomeDelta: 5, traits: ["協商交付"] } },
      { id: "quality", label: "堅持品質並承擔延誤", text: "智力 +4、健康 -2；收入不變，留下專業立場。", effects: { stats: { intellect: 4, health: -2 }, traits: ["品質優先"] } },
      { id: "deadline", label: "照期限完成最低範圍", text: "年收入 +8、健康 -3；短期表現提高，技術成長有限。", effects: { careerIncomeDelta: 8, stats: { health: -3 }, traits: ["期限優先"] } },
    ],
  },
  {
    id: "career-path-headhunter", topic: "career", scope: "person", title: "獵頭帶來一份更高薪的邀請",
    text: "薪資明顯提高，但新公司工時較長，文化與職位穩定度也還不確定。",
    weight: 9, repeatable: true, cooldownYears: 7, maxOccurrences: 3,
    conditions: { minAge: 27, maxAge: 56, careerStatus: "employed", minCareerExperience: 4 },
    choices: [
      { id: "accept", label: "接受高薪職位", text: "年收入 +28、職級 +1；健康 -5，進入高壓工作環境。", effects: { careerIncomeDelta: 28, careerLevelDelta: 1, stats: { health: -5 }, traits: ["高壓高薪"] } },
      { id: "negotiate", label: "以邀請和現公司談條件", text: "年收入 +12；魅力 +3，保留原本團隊與年資。", effects: { careerIncomeDelta: 12, stats: { charm: 3 }, traits: ["薪酬協商"] } },
      { id: "decline", label: "拒絕並維持現況", text: "收入與職級不變；健康 +2，保留較熟悉的生活。", effects: { stats: { health: 2 }, traits: ["重視穩定"] } },
    ],
  },
  {
    id: "career-path-side-business", topic: "career", scope: "person", title: "副業開始出現穩定訂單",
    text: "它還不能取代本業，但已經需要客服、排程和帳務，不再只是偶爾接案。",
    weight: 9, repeatable: true, cooldownYears: 6, maxOccurrences: 3,
    conditions: { minAge: 25, maxAge: 55, careerStatus: "employed", minCareerExperience: 2 },
    choices: [
      { id: "formalize", label: "投入資金建立正式副業", text: "家庭資金 -90、家業 +1；體力 -3、運氣 +3，往後增加家業收入。", effects: { assets: -90, businessLevelDelta: 1, stats: { stamina: -3, luck: 3 }, traits: ["經營副業"] }, requires: { householdAssetsMin: 90 } },
      { id: "limit", label: "限制每月接案量", text: "年收入 +10；體力 -1，保留本業與生活空間。", effects: { careerIncomeDelta: 10, stats: { stamina: -1 }, traits: ["有限接案"] } },
      { id: "close", label: "完成現有訂單後停止", text: "收入不變；健康 +3，把時間重新交還生活。", effects: { stats: { health: 3 }, traits: ["停止副業"] } },
    ],
  },
  {
    id: "career-path-performance-bonus", topic: "career", scope: "person", title: "年度獎金附帶下一年的高目標",
    text: "公司願意給一筆明顯獎金，但希望你承擔更高業績、帶新人或接下困難客戶。",
    weight: 10, repeatable: true, cooldownYears: 5, maxOccurrences: 3,
    conditions: { minAge: 23, maxAge: 60, careerStatus: "employed", minCareerExperience: 2 },
    choices: [
      { id: "target", label: "接受獎金與高目標", text: "家庭資金 +45、年收入 +8；健康 -3，下一年工作壓力提高。", effects: { assets: 45, careerIncomeDelta: 8, stats: { health: -3 }, traits: ["承擔高目標"] } },
      { id: "mentor", label: "改以帶新人換取獎金", text: "家庭資金 +30、魅力 +3、共感 +2；收入不永久提高。", effects: { assets: 30, stats: { charm: 3, empathy: 2 }, traits: ["培養新人"] } },
      { id: "decline", label: "不接受附帶條件的獎金", text: "健康 +2；收入與職責維持原狀。", effects: { stats: { health: 2 }, traits: ["拒絕過量目標"] } },
    ],
  },
  {
    id: "career-path-new-technology", topic: "career", scope: "person", title: "工作流程將全面換成陌生工具",
    text: "新系統可能提高效率，也可能讓熟悉舊方法的人在幾個月內失去位置。",
    weight: 11, repeatable: true, cooldownYears: 6, maxOccurrences: 3,
    conditions: { minAge: 22, maxAge: 62, careerStatuses: ["employed", "self-employed"], minCareerExperience: 1 },
    choices: [
      { id: "early", label: "先完成進階訓練", text: "家庭資金 -35、智力 +5、年收入 +10；健康 -2。", effects: { assets: -35, careerIncomeDelta: 10, stats: { intellect: 5, health: -2 }, traits: ["掌握新工具"] } },
      { id: "peer", label: "和同事組成互助練習", text: "智力 +3、共感 +3；收入暫時不變。", effects: { stats: { intellect: 3, empathy: 3 }, traits: ["同儕轉型"] } },
      { id: "old", label: "留在仍需要舊方法的工作", text: "健康 +1、年收入 -5；短期穩定但未來選擇變少。", effects: { careerIncomeDelta: -5, stats: { health: 1 }, traits: ["維持舊流程"] } },
    ],
  },
  {
    id: "career-path-ethics", topic: "career", scope: "person", title: "主管要求把風險藏在報告後面",
    text: "這能讓專案比較容易通過，但一旦出事，簽名與責任仍會回到參與的人身上。",
    weight: 9, repeatable: true, cooldownYears: 7, maxOccurrences: 2,
    conditions: { minAge: 25, maxAge: 60, careerStatuses: ["employed", "self-employed"], minCareerExperience: 3 },
    choices: [
      { id: "document", label: "留下書面意見並拒絕隱瞞", text: "智力 +4、健康 -2；收入不變，留下可追溯的專業紀錄。", effects: { stats: { intellect: 4, health: -2 }, traits: ["職業倫理"] } },
      { id: "revise", label: "提出不阻礙決策的揭露方式", text: "魅力 +4、智力 +2、年收入 +4；風險被保留在正式文件。", effects: { careerIncomeDelta: 4, stats: { charm: 4, intellect: 2 }, traits: ["風險溝通"] } },
      { id: "comply", label: "照主管要求完成", text: "年收入 +8、健康 -4、共感 -2；短期評價提高。", effects: { careerIncomeDelta: 8, stats: { health: -4, empathy: -2 }, traits: ["服從績效"] } },
    ],
  },
  {
    id: "career-path-flex-work", topic: "career", scope: "person", title: "可以談遠距與彈性工時了",
    text: "通勤時間可能下降，升遷曝光、家庭界線與協作方式也會一起改變。",
    weight: 10, repeatable: true, cooldownYears: 6, maxOccurrences: 2,
    conditions: { minAge: 24, maxAge: 58, careerStatus: "employed", minCareerExperience: 2 },
    choices: [
      { id: "remote", label: "改成以遠距為主", text: "健康 +4、體力 +2、年收入 -4；通勤減少，職場能見度也降低。", effects: { careerIncomeDelta: -4, stats: { health: 4, stamina: 2 }, traits: ["遠距工作"] } },
      { id: "hybrid", label: "談成固定混合安排", text: "健康 +2、魅力 +2；收入不變，保留面對面合作。", effects: { stats: { health: 2, charm: 2 }, traits: ["混合工作"] } },
      { id: "office", label: "維持到辦公室工作的節奏", text: "魅力 +2、年收入 +4、體力 -2；保留較多即時合作。", effects: { careerIncomeDelta: 4, stats: { charm: 2, stamina: -2 }, traits: ["現場協作"] } },
    ],
  },
  {
    id: "career-path-restructure", topic: "career", scope: "person", title: "組織重整後只留下幾個位置",
    text: "留下可能要接更多工作，轉調需要重新適應，自願離開則能取得一筆補償。",
    weight: 8, repeatable: true, cooldownYears: 9, maxOccurrences: 2,
    conditions: { minAge: 28, maxAge: 62, careerStatus: "employed", minCareerExperience: 4 },
    choices: [
      { id: "stay", label: "爭取留下並接下整合工作", text: "職級 +1、年收入 +12、健康 -5；責任明顯增加。", effects: { careerLevelDelta: 1, careerIncomeDelta: 12, stats: { health: -5 }, traits: ["組織整合"] } },
      { id: "transfer", label: "轉到另一個部門", text: "年收入 -5、智力 +4、運氣 +2；重新累積經驗。", effects: { careerIncomeDelta: -5, stats: { intellect: 4, luck: 2 }, traits: ["內部轉調"] } },
      { id: "leave", label: "接受補償並準備下一份工作", text: "家庭資金 +90；暫停工作一年，健康 +3。", effects: { assets: 90, careerPauseYears: 1, stats: { health: 3 }, traits: ["重整離職"] } },
    ],
  },
  {
    id: "career-path-mentorship", topic: "career", scope: "person", title: "後輩希望你成為固定導師",
    text: "教會別人會占用時間，也可能讓你重新整理多年靠直覺完成的方法。",
    weight: 9, repeatable: true, cooldownYears: 6, maxOccurrences: 3,
    conditions: { minAge: 32, maxAge: 65, careerStatuses: ["employed", "self-employed"], minCareerExperience: 7 },
    choices: [
      { id: "formal", label: "建立固定指導計畫", text: "魅力 +4、共感 +4、年收入 +6；體力 -2。", effects: { careerIncomeDelta: 6, stats: { charm: 4, empathy: 4, stamina: -2 }, traits: ["職涯導師"] } },
      { id: "project", label: "只在共同專案中指導", text: "智力 +3、共感 +2；收入與工時不變。", effects: { stats: { intellect: 3, empathy: 2 }, traits: ["實作傳承"] } },
      { id: "decline", label: "坦白目前沒有指導餘裕", text: "健康 +2；不增加新責任。", effects: { stats: { health: 2 }, traits: ["保留工作容量"] } },
    ],
  },
];
